import './styles/ListingsList.scss';

import { useApolloClient, useQuery } from '@apollo/client';
import { CircularProgress, Grid, Icon } from '@mui/material';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';

import USER_QUERY from 'gql/queries/currentUser.gql';
import QUERY from 'gql/queries/getListings.gql';

import { CreateButton } from 'app/components/CreateButton';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { ListingItem } from 'app/components/RichList/items/ListingItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import Belongings from 'assets/belongings.svg';
import BulletinBoard from 'assets/bulletin-board.svg';
import Flat from 'assets/flat.svg';
import Study from 'assets/materials.svg';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import Listing from '../Listing';

interface Props {
  heading?: string;
}

export function ListingsList(props: Props) {
  const client = useApolloClient();
  const headlineInputState = useState('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const userData = client.readQuery({ query: USER_QUERY });
  const getListingsQuery = useQuery<any, any>(QUERY);
  const { refetch, fetchMore } = getListingsQuery;

  usePageHeading(t(translations.layout.menu.bulletinBoard()));

  const cancelListingCreation = (event?, reason?) => {
    if (reason && reason === 'backdropClick') return;
    if (disabledCancel) {
      return;
    }
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
  };

  const richlistFilter: any[] = [
    {
      field: 'title',
      label: t(translations.pages.articles.headline()),
      stateHandler: headlineInputState
    },
    {
      field: 'type',
      label: t(translations.pages.listings.type.study()),
      toggle: {
        value: 'study',
        icon: Study
      }
    },
    {
      field: 'type',
      label: t(translations.pages.listings.type.flat()),
      toggle: {
        value: 'flat',
        icon: Flat
      }
    },
    {
      field: 'type',
      label: t(translations.pages.listings.type.belongings()),
      toggle: {
        value: 'belongings',
        icon: Belongings
      }
    },
    {
      field: 'author',
      label: t(translations.pages.listings.ownListings()),
      toggle: {
        value: userData.currentUser.id,
        icon: <Icon className="fas fa-crown listing__body__own-marker" style={{ width: 'auto' }} />,
        multiple: false,
        onOff: true
      },
      jsonPath: ['id']
    }
  ];

  return (
    <>
      <ErrorBoundary boundaryTag="articleDetailBoundary">
        <Modal
          open={createModalOpen}
          onClose={cancelListingCreation}
          headline={t(translations.pages.listings.create.title())}
          onCancel={() => {
            cancelListingCreation();
          }}
          disabledCancel={disabledCancel}
          onSubmit={() => refetch()}
        >
          <Listing
            onCancel={() => cancelListingCreation()}
            closeHandler={() => cancelListingCreation()}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid container className="listingsList">
        <ErrorBoundary boundaryTag="listingsListBoundary">
          <GQLGuard
            query={getListingsQuery}
            onError={error => {
              if (error.graphQLErrors[0]?.extensions?.status === 404) {
                dispatch(AlertActions.error(`${t(translations.pages.articles.failedLoading())}`));
              } else {
                dispatch(
                  AlertActions.error(
                    `${t(translations.pages.articles.failedLoading())}: ${error.message}`
                  )
                );
              }
              navigate('/', { replace: true });
              return null;
            }}
          >
            {data => (
              <RichList
                filters={richlistFilter}
                controls={
                  <CreateButton
                    onClick={() => {
                      setCreateModalOpen(true);
                    }}
                  >
                    {t(translations.common.create())}
                  </CreateButton>
                }
                onFilterChange={refetch}
              >
                {data.listings.length === 0 ? (
                  <NoItems
                    icon={<img src={BulletinBoard} alt="" style={{ width: '15em' }} />}
                    message={t(translations.pages.listings.noListings())}
                  />
                ) : (
                  <>
                    {data.listings.length === 0 ? (
                      <NoItems />
                    ) : (
                      <>
                        <Grid container>
                          {data.listings.map(l => (
                            <ErrorBoundary key={l.id} boundaryTag="listingItemBoundary">
                              <ListingItem
                                listing={l}
                                key={l.id}
                                isOwnListing={userData.currentUser.id === l.author.id}
                              />
                            </ErrorBoundary>
                          ))}
                        </Grid>
                        <Waypoint
                          onEnter={() => {
                            setLoadingMore(true);
                            fetchMore({
                              variables: {
                                offset: data.listings.length
                              },
                              updateQuery: (prev, { fetchMoreResult }) => {
                                setLoadingMore(false);
                                if (!fetchMoreResult || fetchMoreResult.listings.length === 0) {
                                  return prev;
                                }
                                return Object.assign({}, prev, {
                                  listings: [...prev.listings, ...fetchMoreResult.listings]
                                });
                              }
                            });
                          }}
                        />
                        {loadingMore && (
                          <Grid container justifyContent="center">
                            <CircularProgress />
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
              </RichList>
            )}
          </GQLGuard>
        </ErrorBoundary>
      </Grid>
    </>
  );
}
